import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  EMPTY,
  first,
  map,
  merge,
  noop,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  tap,
} from 'rxjs';
import { IdleTimer } from 'src/app/common/pipes/idle-timer';
import { environment } from 'src/environments/environment';
import { promiseWithCatch, unsubscribeAll } from '../common/app.helpers';
import ProjectLinks from '../common/project.links';

import {
  DialogButton,
  InformationMessage,
  MessageReply,
  RegistrationIndividualUser,
  RegistrationOrganizationUser,
  RegistrationUserBase,
} from '../interfaces';
import { QueryUrlParams } from '../interfaces/query-url-params.interface';
import { NotificationContextAttributesService } from '../notifications/notifications-context-attributes.service';

import { DialogActionTypes } from '../notifications/dialogs';
import { NotificationService } from '../notifications/notifications.service';
import { authContext, skipHandling } from '../services/api-error';
import { ErrorService } from '../services/api-error.service';
import { UrlQueryHandlerService } from '../services/url-query-handler.service';
import {
  AcceptSignupInvitationRequest,
  FullUser,
  LoginRequestAccount,
  LoginResponse,
  SignupIndividualRequest,
  SignupOrganizationRequest,
  UserChangePassword,
} from '../swagger-api';
import { DefaultService as RestApiService } from '../swagger-api/api/default.service';
import { SessionService } from './session.service';

@Injectable()
export class AuthService implements OnDestroy {
  public get navigateQueryUrlParams(): Observable<QueryUrlParams> {
    return this.navigateQueryUrlParams$.asObservable();
  }

  public get reactivatePetitionTriggers$(): Observable<string> {
    return this.errorService.reactivatePetitionTriggers$;
  }

  /**
   * @description Returns the default resend verification email button.
   */
  private get resendVerificationEmailBtn(): DialogButton {
    return this.modalAttributesService.createDialogButton(
      'RESEND_VERIFICATION_EMAIL_BTN_HINT',
      'btn-primary',
      DialogActionTypes.RESEND_VERIFICATION,
    );
  }

  private logoutTrigger: Subject<void> = new Subject();
  private userInactive: Subject<void> = new Subject();
  private userActivityDetected: Subject<void> = new Subject();
  private navigateQueryUrlParams$: ReplaySubject<QueryUrlParams> = new ReplaySubject(1);

  private timer: IdleTimer | null = null;
  private queryParams: QueryUrlParams | null = null;

  private subscriptions$: Subscription[] = [];

  private apiService = inject(RestApiService);
  private sessionService = inject(SessionService);
  private notificationService = inject(NotificationService);
  private errorService = inject(ErrorService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private modalAttributesService = inject(NotificationContextAttributesService);
  private translateService = inject(TranslateService);
  private modalService = inject(NgbModal);
  private urlQueryHandlerService = inject(UrlQueryHandlerService);

  public constructor() {
    this.authenticationLostSubscription();
    this.userActivitySubscriptionInit();
    this.externalSessionProlongationSubscriptionInit();
    this.subscribeOnQueryParamsChanges();

    this.subscriptions$.push(
      this.errorService.emailNotVerifiedTrigger$.subscribe((email: string) => {
        this.resendVerificationEmail(email);
      }),
      this.errorService.requestChangedEmailVerification.subscribe((email: string) =>
        this.requestChangedEmailVerification(email),
      ),
    );
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
    this.userInactive.complete();
    this.userActivityDetected.complete();
  }

  public isAuthenticated(): boolean {
    return this.sessionService.isSessionActive();
  }

  /**
   * @description Initiates API call resulting in authorizing the user in the system.
   * @param { LoginRequestAccount } loginUser
   * @param { string } timezone
   */
  public login(loginUser: LoginRequestAccount, timezone?: string): void {
    this.apiService
      .restAuthLoginPost({ account: loginUser, timezone }, authContext(loginUser.email))
      .pipe(
        catchError(() => EMPTY),
        tap(() => {
          this.authenticationLostSubscription();
        }),
      )
      .subscribe((response: LoginResponse) => {
        this.sessionService.sessionStorageData = response;
        this.queryParams
          ? this.fetchUserData(response.user_id, this.queryParams)
          : this.fetchUserData(response.user_id);
      });
  }

  /**
   * @description Initiates API call change password for current user.
   * @param { UserChangePassword } data
   */
  public changePassword(data: UserChangePassword): void {
    this.apiService
      .restUserChangePasswordPost(data)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.notificationService.showSuccessfulMessage('CHANGE_PASSWORD_DESCRIPTION');

        promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_PROFILE_EDIT));
      });
  }

  /**
   * @description Clears session storage and navigates to default page.
   */
  public clientSideLogout(): void {
    this.stopInactivityTimer();
    this.sessionService.endSession();

    promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT));
  }

  /**
   * @description Drops session at server side
   */
  public serverSideLogout(): void {
    if (this.sessionService.isSessionActive()) {
      this.apiService
        .restAuthLogoutPost()
        .pipe(catchError(() => EMPTY))
        .subscribe();
    }
  }

  /**
   * @description Initiates API call resulting in creating a new individual user in the system.
   * @param { RegistrationIndividualUser } registrationUser
   * @param timezone
   */
  public registerIndividual(registrationUser: RegistrationIndividualUser, timezone?: string): void {
    const newIndividualUser: SignupIndividualRequest = {
      individual: { alias: registrationUser.alias },
      account: { email: registrationUser.email, password: registrationUser.password },
      timezone,
    };

    this.apiService
      .restAuthSignupIndividualPost(newIndividualUser)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.showSuccessDialog(this.registrationSuccessfulDialogCall(registrationUser), registrationUser.email);
        promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_LOGIN));
      });
  }

  /**
   * @description Initiates API call resulting in creating a new organization user in the system.
   * @param { RegistrationIndividualUser } registrationUser
   * @param timezone
   */
  public registerOrganization(registrationUser: RegistrationOrganizationUser, timezone?: string): void {
    const newOrganizationUser: SignupOrganizationRequest = {
      organization: { organization_name: registrationUser.organizationName },
      account: { email: registrationUser.email, password: registrationUser.password },
      timezone,
    };

    this.apiService
      .restAuthSignupOrganizationPost(newOrganizationUser)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.showSuccessDialog(this.registrationSuccessfulDialogCall(registrationUser), registrationUser.email);
        promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_LOGIN));
      });
  }

  /**
   * @description Initiates API call triggering password reset for provided email if it exists.
   * @param { string } email
   */
  public resetPassword(email: string): void {
    this.apiService
      .restAuthResetPasswordPost({ email }, authContext(email))
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_LOGIN));
      });
  }

  /**
   * @description Initiates API call resulting in setting a new password for user account.
   * @param { string } password
   * @param { string } token
   */
  public recoverPassword(password: string, token: string): void {
    this.apiService
      .restAuthResetPasswordPut({ password, token })
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_LOGIN));
      });
  }

  public refetchUserDataAfter2FA(redirectPage?: string, params?: QueryUrlParams): void {
    this.apiService
      .restUserGet(this.sessionService.userId)
      .pipe(catchError(() => EMPTY))
      .subscribe((user: FullUser) => {
        this.sessionService.user = user;
        if (redirectPage && !params) {
          promiseWithCatch(this.router.navigate([redirectPage]));
        } else {
          this.navigateByQueryParams();
        }
      });
  }

  public recoverUserData(userId: string): void {
    this.apiService.restAuthProlongPost().subscribe({
      next: () => {
        // It's possible the session was cleared by now asynchronously
        if (this.sessionService.isSessionActive()) {
          this.sessionService.markSessionProlonged();
          this.fetchUserData(userId);
        }
      },
      error: (error: HttpErrorResponse) => {
        this.handleErrorLogout(error);
      },
    });
  }

  public navigateByQueryParams(): void {
    this.subscriptions$.push(
      this.navigateQueryUrlParams.subscribe((params: QueryUrlParams) => {
        this.urlQueryHandlerService.proceedUrlQueryParams(params);
      }),
    );
  }

  public showSendMessageRefuseModal(previousPage: string): void {
    this.showSuccessDialog(
      this.notificationService.dialogDefault({
        title: 'SEND_MESSAGE_REFUSE_TITLE',
        description: 'SEND_MESSAGE_REFUSE_DESCRIPTION',
        buttons: [
          this.modalAttributesService.createDialogButton(
            'AUTHORIZE_BTN',
            'btn-primary',
            DialogActionTypes.GO_TO_SECOND_AUTH_PAGE,
          ),
          ...this.modalAttributesService.getDefaultCloseAttr('btn-secondary'),
        ],
      }),
      previousPage,
    );
  }

  public validateResetPasswordToken(token: string): void {
    this.apiService.restAuthValidateResetPasswordTokenPost({ token }).subscribe({
      next: () => noop,
      error: () => {
        promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_LOGIN));
      },
    });
  }

  public validateInvitationToken(token: string): void {
    this.apiService.restAuthValidateSignupInvitationTokenPost({ token }).subscribe({
      next: () => noop,
      error: () => {
        promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_LOGIN));
      },
    });
  }

  /**
   * @description Initiates API call resulting in creating an account for invited user.
   * @param { AcceptSignupInvitationRequest } signupByInvitationRequest
   * @param { boolean } dialogDescription
   */
  public signupByInvitation(signupByInvitationRequest: AcceptSignupInvitationRequest): void {
    this.apiService
      .restAuthAcceptSignupInvitationPost(signupByInvitationRequest)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        promiseWithCatch(
          this.router.navigate([ProjectLinks.LINK_LOGIN], {
            queryParams: { returnUrl: ProjectLinks.LINK_PROFILE_VIEW },
          }),
        );
      });
  }

  /**
   * @description Initiates API call resulting in fetching a specified user data by his id.
   *              In successful case also saves the last visited by user page by link
   *              that will be opened after passing the second auth level.
   */
  private fetchUserData(userId: string, previousPage?: QueryUrlParams): void {
    this.apiService.restUserGet(userId).subscribe({
      next: (user: FullUser) => {
        this.sessionService.user = user;

        this.initAppInfo();
        if (previousPage) {
          promiseWithCatch(
            this.router.navigate([ProjectLinks.LINK_SECOND_AUTH_LEVEL], {
              state: previousPage,
            }),
          );
        }
      },
      error: (error: HttpErrorResponse) => {
        this.handleErrorLogout(error);
      },
    });
  }

  private handleErrorLogout(error: HttpErrorResponse): void {
    // In safari, when a request is canceled it triggers this handler, but it doesn't mean we have to log out.
    // We need to make sure the error comes from a bad token or other security measure from the client (4xx).

    if (!error.status) return;
    if (error.status < 400 || error.status > 499) return;

    this.sessionService.endSession();
  }

  /**
   * @description Initiates an API call resulting in resending the verification link
   *              to the user's email.
   * @param { string } email
   */
  private resendVerificationEmail(email: string): void {
    this.apiService
      .restAuthResendVerificationEmailPost({ email })
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.notificationService.showSuccessfulMessage('RESEND_VERIFICATION_EMAIL_SUCCESSFUL_DESCRIPTION_HINT');
      });
  }

  private requestChangedEmailVerification(email: string): void {
    this.apiService
      .restAuthRequestChangedEmailVerificationPost({ email })
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.notificationService.showSuccessfulMessage('RESEND_CHANGED_EMAIL_VERIFICATION_SUCCESSFUL_DESCRIPTION_HINT');
      });
  }

  /**
   * @description Subscribes on dialog$ result and processes it.
   * @param { Observable<string | boolean> } dialog$
   * @param dialogActionParam
   */
  private showSuccessDialog(dialog$: Observable<string | boolean>, dialogActionParam?: string): void {
    dialog$.pipe(first()).subscribe((result: string | boolean) => {
      const actions: { [key: string]: () => Promise<boolean> } = {
        [DialogActionTypes.RESEND_VERIFICATION]: () => {
          this.resendVerificationEmail(dialogActionParam);
          return Promise.resolve(true);
        },

        [DialogActionTypes.GO_TO_HOMEPAGE]: () => this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT),
        [DialogActionTypes.GO_TO_REGISTRATION]: () => this.router.navigateByUrl(ProjectLinks.LINK_REGISTRATION),
        [DialogActionTypes.GO_TO_LOGIN_PAGE]: () => this.router.navigateByUrl(ProjectLinks.LINK_LOGIN),
        [DialogActionTypes.GO_TO_SECOND_AUTH_PAGE]: () => {
          this.modalService.dismissAll();

          return this.router.navigate([ProjectLinks.LINK_SECOND_AUTH_LEVEL], {
            state: {
              state: true,
              previousPage: dialogActionParam,
            },
          });
        },
      };

      if (result) {
        actions[String(result)] && promiseWithCatch(actions[String(result)]());
      }
    });
  }

  private registrationSuccessfulDialogCall(registrationUser: RegistrationUserBase): Observable<string | boolean> {
    return this.notificationService.dialogDefault({
      description: this.translateService.instant('SIGN_UP_SUCCESSFUL_OPERATION_DESCRIPTION_HINT', {
        email: registrationUser.email,
      }) as string,
      title: 'SIGN_UP_SUCCESSFUL_OPERATION_TITLE_HINT',
      buttons: [
        this.modalAttributesService.createDialogButton(
          'SIGN_UP_DIALOG_GOT_TO_HOMEPAGE_BTN_HINT',
          'btn-outline-primary',
          DialogActionTypes.GO_TO_HOMEPAGE,
        ),
        this.resendVerificationEmailBtn,
      ],
    });
  }

  private initAppInfo(): void {
    this.initializeInactivityTimer();
    this.getQueryUrlParams();
  }

  private getQueryUrlParams(): void {
    this.activatedRoute.queryParams.pipe(first()).subscribe((params: Params) => {
      this.navigateQueryUrlParams$.next(params);
    });
  }

  private initializeInactivityTimer(): void {
    this.timer = new IdleTimer(environment.sessionActivityTimerDelayMs, this.userInactive, this.userActivityDetected);
  }

  private stopInactivityTimer(): void {
    if (this.timer) {
      this.timer.cleanUp();
    }
    this.timer = null;
  }

  private authenticationLostSubscription(): void {
    const replies: MessageReply[] = [
      {
        label: 'BTN_OK_HINT',
        callback: () => noop,
      },
      {
        label: 'LOGIN_FORM_TEXT',
        callback: () => promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_LOGIN)),
      },
    ];

    const userInactiveMessage: InformationMessage = {
      title: 'SESSION_EXPIRED_TITLE_HINT',
      content: [{ type: 'text', value: 'SESSION_EXPIRED_DESCRIPTION_HINT' }],
      level: 'ERROR',
      replies,
    };

    const sessionIsNotRecognizedMessage: InformationMessage = {
      title: 'SESSION_IS_NOT_RECOGNIZED_TITLE',
      content: [{ type: 'text', value: 'SESSION_IS_NOT_RECOGNIZED_DESCRIPTION' }],
      level: 'ERROR',
      replies,
    };

    this.subscriptions$.push(
      merge(
        this.userInactive.pipe(map(() => userInactiveMessage)),
        this.logoutTrigger.pipe(map(() => sessionIsNotRecognizedMessage)),
        this.errorService.authorizationLostTrigger$.pipe(
          map((message: InformationMessage) => ({ ...message, replies })),
        ),
      )
        .pipe(
          first(),
          tap((message: InformationMessage) => {
            if (this.sessionService.isSessionActive()) {
              this.notificationService.informationDialog(message);
            }
            this.clientSideLogout();
          }),
        )
        .subscribe(),
    );
  }

  private userActivitySubscriptionInit(): void {
    this.subscriptions$.push(
      this.userActivityDetected.subscribe(() => {
        if (this.sessionService.isSessionActive()) {
          this.apiService
            // skip for the long operation
            .restAuthProlongPost(skipHandling(true))
            .pipe(
              catchError(() => {
                this.logoutTrigger.next();
                return EMPTY;
              }),
            )
            .subscribe(() => {
              // It's possible the session was cleared by now asynchronously
              if (this.sessionService.isSessionActive()) {
                this.sessionService.markSessionProlonged();
              } else {
                this.logoutTrigger.next();
              }
            });
        } else {
          this.logoutTrigger.next();
        }
      }),
    );
  }

  private externalSessionProlongationSubscriptionInit(): void {
    this.subscriptions$.push(
      this.sessionService.sessionProlongedInOtherTab.subscribe(() => {
        if (this.timer) {
          this.timer.restart();
        }
      }),
    );
  }

  private subscribeOnQueryParamsChanges(): void {
    // TODO_3633: get rid of useValue in the tests  { provide: ActivatedRoute, useValue: {} }
    if (this.activatedRoute && this.activatedRoute.queryParams) {
      const subscription: Subscription = this.activatedRoute.queryParams.subscribe((params: QueryUrlParams) => {
        this.queryParams = params;
        if (this.isAuthenticated && params && params.returnUrl) {
          this.urlQueryHandlerService.proceedUrlQueryParams(params);
        }
      });
      this.subscriptions$.push(subscription);
    }
  }
}
